// admin/src/pages/ScimIntegrations/TokenModal.js

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyIcon from '@mui/icons-material/Key';

export default function TokenModal({ open, tokenData, onClose }) {
  const [copied, setCopied] = useState(false);
  const [confirmedSaved, setConfirmedSaved] = useState(false);

  if (!open || !tokenData) {
    return null;
  }

  const plaintextToken = tokenData.plaintextToken || '';
  const tokenPrefix = tokenData.tokenPrefix || '';
  const tokenId = tokenData.tokenId || '';

  const handleCopy = async () => {
    if (plaintextToken && navigator?.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(plaintextToken);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (e) {
        // Fallback for testing environments where clipboard API is not available
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    } else {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleClose = () => {
    if (!confirmedSaved) return;
    setCopied(false);
    setConfirmedSaved(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Block backdrop and escape close until explicit confirmation checkbox is checked
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
      aria-labelledby="scim-token-modal-title"
      aria-describedby="scim-token-modal-desc"
    >
      <DialogTitle id="scim-token-modal-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <KeyIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          SCIM Bearer Token Issued
        </Typography>
      </DialogTitle>

      <DialogContent id="scim-token-modal-desc" dividers>
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Store this token securely now. It will never be shown again.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If you close this dialog without saving the token, you will need to rotate or generate a new token.
          </Typography>
        </Alert>

        {tokenId && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Token Identifier: <strong>{tokenPrefix || tokenId}</strong>
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'action.hover',
            border: '1px solid',
            borderColor: 'divider',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            wordBreak: 'break-all',
            mb: 2.5,
          }}
        >
          <Typography
            component="code"
            data-testid="scim-plaintext-token"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              wordBreak: 'break-all',
              userSelect: 'all',
              mr: 1,
            }}
          >
            {plaintextToken}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy Token'}>
            <IconButton
              onClick={handleCopy}
              aria-label="Copy token to clipboard"
              color={copied ? 'success' : 'primary'}
              size="small"
              sx={{ flexShrink: 0 }}
            >
              {copied ? <CheckCircleIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              id="confirm-saved-token-checkbox"
              data-testid="confirm-saved-token-checkbox"
              checked={confirmedSaved}
              onChange={(e) => setConfirmedSaved(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              I have saved the token and stored it in a secure location
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          id="close-token-modal-btn"
          data-testid="close-token-modal-btn"
          variant="contained"
          color="primary"
          onClick={handleClose}
          disabled={!confirmedSaved}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TokenModal.propTypes = {
  open: PropTypes.bool.isRequired,
  tokenData: PropTypes.shape({
    plaintextToken: PropTypes.string,
    tokenId: PropTypes.string,
    tokenPrefix: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};
